import { faker } from '@faker-js/faker'
import { addDays, subDays, addHours } from 'date-fns'
import type {
  Asset,
  AssetStatus,
  MaintenanceRecord,
  MaintenanceType,
  FailureCategory,
  SparePartUsage,
  MaintenanceSchedule,
  ProductCategory,
} from '../types'

const assetTypes = [
  'CNC Machine',
  'Industrial Robot',
  'Conveyor System',
  'Packaging Machine',
  'Assembly Line',
  'Injection Molder',
  'Heat Treatment Furnace',
  'Welding Robot',
  'Quality Control Scanner',
  'Automated Storage System',
]

const manufacturers = [
  'ABB',
  'Siemens',
  'Fanuc',
  'Kuka',
  'Mitsubishi',
  'Schneider',
  'Rockwell',
  'Honeywell',
  'Emerson',
  'Bosch',
]

const sites = [
  'Main Production Facility',
  'Assembly Plant North',
  'Assembly Plant South',
  'Distribution Center',
  'R&D Facility',
]

const areas = [
  'Production Line A',
  'Production Line B',
  'Assembly Area',
  'Packaging Zone',
  'Quality Control',
  'Warehouse',
  'Maintenance Shop',
]

const technicians = [
  'John Smith',
  'Maria Garcia',
  'David Chen',
  'Sarah Johnson',
  'Michael Brown',
  'Emma Wilson',
  'James Taylor',
  'Lisa Anderson',
  'Robert Martinez',
  'Jennifer Lee',
]

const spareParts = [
  { name: 'Hydraulic Pump', category: 'machinery_parts' as ProductCategory },
  { name: 'Control Board', category: 'custom_components' as ProductCategory },
  { name: 'Bearing Assembly', category: 'machinery_parts' as ProductCategory },
  { name: 'Servo Motor', category: 'machinery_parts' as ProductCategory },
  { name: 'Sensor Array', category: 'custom_components' as ProductCategory },
  { name: 'Belt Drive', category: 'machinery_parts' as ProductCategory },
  { name: 'Power Supply', category: 'custom_components' as ProductCategory },
  { name: 'Pneumatic Valve', category: 'machinery_parts' as ProductCategory },
  { name: 'Gear Set', category: 'machinery_parts' as ProductCategory },
  { name: 'Touch Screen', category: 'custom_components' as ProductCategory },
]

function generateAsset(parentId?: string): Asset {
  const installationDate = subDays(new Date(), faker.number.int({ min: 180, max: 1095 }))
  const lastMaintenanceDate = subDays(new Date(), faker.number.int({ min: 0, max: 90 }))
  
  const status: AssetStatus = faker.helpers.arrayElement([
    'operational',
    'maintenance',
    'repair',
    'standby',
    'decommissioned',
  ])

  return {
    id: faker.string.uuid(),
    modelNumber: `${faker.string.alpha({ length: 2, casing: 'upper' })}-${faker.number.int({ min: 1000, max: 9999 })}`,
    serialNumber: faker.string.alphanumeric(10).toUpperCase(),
    name: faker.helpers.arrayElement(assetTypes),
    type: faker.helpers.arrayElement(assetTypes),
    manufacturer: faker.helpers.arrayElement(manufacturers),
    installationDate,
    location: {
      site: faker.helpers.arrayElement(sites),
      area: faker.helpers.arrayElement(areas),
      coordinates: [
        faker.number.float({ min: -90, max: 90, multipleOf: 0.000001 }),
        faker.number.float({ min: -180, max: 180, multipleOf: 0.000001 }),
      ],
    },
    status,
    specifications: {
      dimensions: `${faker.number.int({ min: 100, max: 500 })}x${faker.number.int({ min: 100, max: 500 })}x${faker.number.int({ min: 100, max: 500 })} cm`,
      weight: faker.number.int({ min: 100, max: 5000 }),
      power: `${faker.number.int({ min: 1, max: 50 })} kW`,
      capacity: `${faker.number.int({ min: 100, max: 1000 })} units/hour`,
    },
    lastMaintenanceDate,
    nextMaintenanceDate: addDays(lastMaintenanceDate, faker.number.int({ min: 30, max: 180 })),
    operatingHours: faker.number.int({ min: 1000, max: 50000 }),
    parentAssetId: parentId,
  }
}

function generateSparePartUsage(assetId: string, maintenanceId: string, date: Date): SparePartUsage {
  const part = faker.helpers.arrayElement(spareParts)
  return {
    id: faker.string.uuid(),
    partNumber: `SP-${faker.string.alphanumeric(6).toUpperCase()}`,
    name: part.name,
    quantity: faker.number.int({ min: 1, max: 5 }),
    unitCost: faker.number.float({ min: 100, max: 5000, multipleOf: 0.01 }),
    assetId,
    maintenanceId,
    usageDate: date,
    category: part.category,
  }
}

function generateMaintenanceRecord(assetId: string, isCompleted = true): MaintenanceRecord {
  const startDate = subDays(new Date(), faker.number.int({ min: 0, max: 180 }))
  const endDate = addHours(startDate, faker.number.int({ min: 2, max: 48 }))
  
  const type: MaintenanceType = faker.helpers.arrayElement([
    'preventive',
    'corrective',
    'predictive',
    'upgrade',
    'calibration',
  ])

  const failureCategory: FailureCategory | undefined = type === 'corrective'
    ? faker.helpers.arrayElement([
        'mechanical',
        'electrical',
        'hydraulic',
        'pneumatic',
        'software',
        'structural',
        'wear',
        'operator_error',
        'environmental',
      ])
    : undefined

  const sparePartsUsed = Array.from(
    { length: faker.number.int({ min: 0, max: 3 }) },
    () => generateSparePartUsage(assetId, faker.string.uuid(), startDate)
  )

  return {
    id: faker.string.uuid(),
    assetId,
    type,
    startDate,
    endDate,
    description: faker.lorem.sentence(),
    technician: faker.helpers.arrayElement(technicians),
    status: isCompleted ? 'completed' : faker.helpers.arrayElement(['scheduled', 'in_progress']),
    sparePartsUsed,
    findings: faker.lorem.paragraph(),
    recommendations: faker.lorem.paragraph(),
    failureCategory,
    failureDescription: failureCategory ? faker.lorem.paragraph() : undefined,
    downtime: faker.number.float({ min: 1, max: 48, multipleOf: 0.1 }),
    cost: sparePartsUsed.reduce((sum, part) => sum + (part.quantity * part.unitCost), 0),
  }
}

function generateMaintenanceSchedule(assetId: string): MaintenanceSchedule {
  const plannedStartDate = addDays(new Date(), faker.number.int({ min: 1, max: 90 }))
  const estimatedDuration = faker.number.int({ min: 2, max: 48 })

  return {
    id: faker.string.uuid(),
    assetId,
    type: faker.helpers.arrayElement([
      'preventive',
      'predictive',
      'upgrade',
      'calibration',
    ]),
    plannedStartDate,
    plannedEndDate: addHours(plannedStartDate, estimatedDuration),
    description: faker.lorem.sentence(),
    assignedTechnician: faker.helpers.arrayElement(technicians),
    estimatedDuration,
    requiredParts: Array.from(
      { length: faker.number.int({ min: 0, max: 3 }) },
      () => ({
        partNumber: `SP-${faker.string.alphanumeric(6).toUpperCase()}`,
        quantity: faker.number.int({ min: 1, max: 5 }),
      })
    ),
    priority: faker.helpers.arrayElement(['low', 'medium', 'high', 'critical']),
    status: faker.helpers.arrayElement(['scheduled', 'delayed']),
  }
}

export function generateAssetData() {
  // Generate main assets
  const mainAssets = Array.from(
    { length: faker.number.int({ min: 10, max: 15 }) },
    () => generateAsset()
  )

  // Generate sub-components for each main asset
  const assets = mainAssets.flatMap(mainAsset => {
    const subComponents = Array.from(
      { length: faker.number.int({ min: 2, max: 5 }) },
      () => {
        const asset = generateAsset(mainAsset.id)
        return asset
      }
    )
    return [{ ...mainAsset, children: subComponents }, ...subComponents]
  })

  // Generate maintenance records
  const maintenanceHistory = assets.flatMap(asset =>
    Array.from(
      { length: faker.number.int({ min: 3, max: 8 }) },
      () => generateMaintenanceRecord(asset.id)
    )
  )

  // Generate maintenance schedules
  const maintenanceSchedules = assets.flatMap(asset =>
    Array.from(
      { length: faker.number.int({ min: 1, max: 3 }) },
      () => generateMaintenanceSchedule(asset.id)
    )
  )

  return {
    assets,
    maintenanceHistory,
    maintenanceSchedules,
  }
} 